/**
 * Per-module copy for the simplified landing — transcribed verbatim from the
 * design's `MODS` table (`Simplified Landing - Finance Pilot.html` lines 100-104).
 * `core` is labelled "Platform" in the UI. The comp's `code` module has no
 * server-side counterpart yet, so it is not listed here; unknown modules fall
 * back to `all`.
 */

export interface SimpleStarter {
  /** Eyebrow keyword shown above the starter title (comp `.starter .k`). */
  kind: string;
  label: string;
  description: string;
  /** What gets said into the conversation when the starter is clicked. */
  message: string;
}

export interface SimpleModuleCopy {
  title: string;
  placeholder: string;
  starters: SimpleStarter[];
}

export const SIMPLE_MODULE_COPY: Record<'all' | 'core' | 'finance', SimpleModuleCopy> = {
  all: {
    title: 'Everything',
    placeholder:
      'Ask anything — “status of the system”, “test a breakout idea on ETH”, “build a dashboard for my paper trades”…',
    starters: [
      {
        kind: 'status',
        label: 'Give me a status of the system',
        description: 'Projects, strategies, code previews, what is waiting on you.',
        message: 'Give me a status of the system',
      },
      {
        kind: 'browse',
        label: 'Show all finance strategies',
        description: 'Pick one to dive into, or compare two.',
        message: 'Show all finance strategies',
      },
      {
        kind: 'build',
        label: 'Evaluate a new strategy idea',
        description: 'I design it, backtest it, and recommend a next step.',
        message: 'Evaluate a new strategy idea',
      },
      {
        kind: 'decide',
        label: 'What is waiting on me?',
        description: 'Approvals and decisions only a human can make.',
        message: 'What is waiting on me?',
      },
    ],
  },
  finance: {
    title: 'Finance',
    placeholder:
      'Ask anything — “compare my crypto strategies”, “test a breakout idea on ETH”, “what changed since yesterday?”',
    starters: [
      {
        kind: 'status',
        label: 'Give me a status of the system',
        description: 'Projects, strategies, what is waiting on you.',
        message: 'Give me a status of the system',
      },
      {
        kind: 'browse',
        label: 'Show all finance strategies',
        description: 'Pick one to dive into, or compare two.',
        message: 'Show all finance strategies',
      },
      {
        kind: 'build',
        label: 'Evaluate a new strategy idea',
        description: 'I design it, backtest it, and recommend a next step.',
        message: 'Evaluate a new strategy idea',
      },
      {
        kind: 'decide',
        label: 'What is waiting on me?',
        description: 'Approvals and decisions only a human can make.',
        message: 'What is waiting on me?',
      },
    ],
  },
  core: {
    title: 'Platform',
    placeholder:
      'Ask about the platform — “who is on my team”, “connect my broker”, “schedule the brief every Monday”…',
    starters: [
      {
        kind: 'team',
        label: 'Who is working for me right now?',
        description: 'Specialists, what they are doing, and what they cost.',
        message: 'Who is working for me right now?',
      },
      {
        kind: 'schedule',
        label: 'Run the research brief every Monday 7:00',
        description: 'I create the recurring workflow — you never see the builder.',
        message: 'Run the research brief every Monday 7:00',
      },
      {
        kind: 'connect',
        label: 'Connect my broker',
        description: 'Guided connection with the permissions explained.',
        message: 'Connect my broker',
      },
    ],
  },
};

export function copyFor(moduleId: string): SimpleModuleCopy {
  return SIMPLE_MODULE_COPY[moduleId as keyof typeof SIMPLE_MODULE_COPY] ?? SIMPLE_MODULE_COPY.all;
}

/** Short rail tag for a module id (comp `.mt`), shown only when the scope is "Everything". */
export function moduleTag(moduleId: string): string {
  return moduleId === 'finance' ? 'fin' : moduleId;
}
